"use client";

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Bot, ChevronLeft, Cpu, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import { CommandAgentSharingCard } from "@/components/command-team/command-agent/cards/command-agent-sharing-card";
import { AnimatedPage } from "@/components/shared/animated-page";
import { FormField } from "@/components/shared/form-fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { Textarea } from "@/components/ui/textarea";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { fetchData, post } from "@/utils/api-handler";

interface RolesListResponse {
    items?: Array<{ id?: string; title?: string }>;
}

type CreateCommandAgentPayload = {
    name: string;
    description: string | null;
    role: string;
    persona: string;
    restrictions: string | null;
    visibility: AppIntegrationVisibility;
    shared_roles?: string[];
};

interface CreateCommandAgentResponse {
    id: string;
    name?: string;
}

interface CreateCommandAgentPanelProps {
    className?: string;
    listPath?: string;
    agentDetailBasePath?: string;
}

function SettingsCard({
    icon,
    title,
    subtitle,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
            <div className="border-b px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
                        {icon}
                    </span>
                    <div>
                        <h2 className="text-sm font-semibold">{title}</h2>
                        {subtitle ? (
                            <p className="text-xs text-muted-foreground">{subtitle}</p>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="px-5 py-4">{children}</div>
        </div>
    );
}

export function CreateCommandAgentPanel({
    className,
    listPath = "/workforce/command-team-old/agents",
    agentDetailBasePath = "/workforce/command-team-old/agents",
}: CreateCommandAgentPanelProps) {
    const router = useRouter();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        visibility: "PERSONAL" as AppIntegrationVisibility,
        shared_roles: [] as string[],
    });

    const [errors, setErrors] = useState({
        name: "",
        description: "",
        role: "",
        persona: "",
        restrictions: "",
        visibility: "",
        shared_roles: "",
    });

    const { data: rolesQueryData, isLoading: rolesLoading, hasPermission: canListRoles } =
        useProtectedQuery<RolesListResponse | Array<{ id?: string; title?: string }>>({
            queryKey: ["command-agent-role-options"],
            requiredPermissions: ["LIST_ROLES"],
            queryFn: async () => {
                const params = new URLSearchParams({ page: "1", limit: "500" });
                return fetchData(`/api/rbac/roles?${params.toString()}`);
            },
        });

    const roleOptions = useMemo(() => {
        const raw = rolesQueryData;
        const items = Array.isArray(raw) ? raw : raw?.items ?? [];
        return items
            .filter((role): role is { id: string; title?: string } => Boolean(role.id))
            .map((role) => ({ label: role.title?.trim() || role.id, value: role.id }));
    }, [rolesQueryData]);

    const createAgentMutation = useProtectedMutation({
        mutationFn: (payload: CreateCommandAgentPayload) =>
            post("/api/command-team/agents", payload),
        requiredPermissions: ["CREATE_COMMAND_AGENT"],
        onSuccess: (response: { data?: CreateCommandAgentResponse }) => {
            queryClient.invalidateQueries({ queryKey: ["command-agents"] });
            queryClient.invalidateQueries({ queryKey: ["command-agents-sidebar"] });
            queryClient.invalidateQueries({ queryKey: ["trigger-route-configuration"] });

            const created = response?.data;
            const name = created?.name ?? formData.name;
            toast.success(`"${name}" created`, {
                description: "Active and ready in Command chat.",
            });

            if (created?.id) {
                router.push(
                    `${agentDetailBasePath}/${created.id}?name=${encodeURIComponent(name)}`
                );
                return;
            }

            router.push(listPath);
        },
        onError: (error: {
            response?: { data?: { detail?: Array<{ loc: string[]; msg: string }>; message?: string } };
        }) => {
            const newErrors = {
                name: "",
                description: "",
                role: "",
                persona: "",
                restrictions: "",
                visibility: "",
                shared_roles: "",
            };

            const validationErrors = error.response?.data?.detail;
            if (Array.isArray(validationErrors)) {
                validationErrors.forEach((err) => {
                    const field = err.loc[1];
                    const message = err.msg;
                    if (field in newErrors) {
                        newErrors[field as keyof typeof newErrors] = message;
                    }
                });
                setErrors(newErrors);
                return;
            }

            toast.error("Failed to create command agent", {
                description: error.response?.data?.message || "An unexpected error occurred",
            });
        },
    });

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field as keyof typeof errors]) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleSubmit = () => {
        setErrors({
            name: "",
            description: "",
            role: "",
            persona: "",
            restrictions: "",
            visibility: "",
            shared_roles: "",
        });

        const payload: CreateCommandAgentPayload = {
            name: formData.name.trim(),
            description: formData.description.trim() === "" ? null : formData.description.trim(),
            role: formData.role.trim(),
            persona: formData.persona.trim(),
            restrictions: formData.restrictions.trim() === "" ? null : formData.restrictions.trim(),
            visibility: formData.visibility,
        };

        if (formData.visibility === "TEAM" && formData.shared_roles.length > 0) {
            payload.shared_roles = formData.shared_roles;
        }

        createAgentMutation.mutate(payload);
    };

    const canSubmit =
        formData.name.trim().length > 0 &&
        formData.role.trim().length > 0 &&
        !createAgentMutation.isPending;

    return (
        <AnimatedPage className={className ?? "flex min-h-0 flex-1 flex-col overflow-hidden"}>
            <div className="shrink-0 border-b px-4 py-3 sm:px-6 sm:py-5">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => router.push(listPath)}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                        aria-label="Back to Command agents"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div className="ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
                        <Bot className="size-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold tracking-tight sm:text-2xl">
                            New Command Agent
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Persona, behaviour and sharing — configure the essentials here, then
                            fine-tune LLM and connections in settings.
                        </p>
                    </div>
                </div>
            </div>

            <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                {createAgentMutation.isPending ? <Loader /> : null}
                <div className="mx-auto w-full max-w-[760px] space-y-4">
                    <SettingsCard
                        icon={<Bot className="size-4 text-primary" />}
                        title="General"
                        subtitle="Who this digital worker is."
                    >
                        <div className="space-y-4">
                            <FormField label="Name" error={errors.name}>
                                <Input
                                    value={formData.name}
                                    onChange={(event) => handleInputChange("name", event.target.value)}
                                    placeholder="e.g. Pipeline Briefing Agent"
                                    autoFocus
                                />
                            </FormField>
                            <FormField label="Role" error={errors.role}>
                                <Input
                                    value={formData.role}
                                    onChange={(event) => handleInputChange("role", event.target.value)}
                                    placeholder="e.g. Executive briefings"
                                />
                            </FormField>
                            <FormField label="Description" error={errors.description}>
                                <Textarea
                                    value={formData.description}
                                    onChange={(event) =>
                                        handleInputChange("description", event.target.value)
                                    }
                                    rows={2}
                                    placeholder="What this agent handles in Command chat"
                                />
                            </FormField>
                        </div>
                    </SettingsCard>

                    <CommandAgentSharingCard
                        idPrefix="create-command-agent-page"
                        visibility={formData.visibility}
                        sharedRoles={formData.shared_roles}
                        canListRoles={canListRoles}
                        rolesLoading={rolesLoading}
                        roleOptions={roleOptions}
                        errors={{
                            visibility: errors.visibility,
                            shared_roles: errors.shared_roles,
                        }}
                        onVisibilityChange={(visibility) => {
                            setFormData((prev) => ({
                                ...prev,
                                visibility,
                                shared_roles: visibility === "TEAM" ? prev.shared_roles : [],
                            }));
                            setErrors((prev) => ({
                                ...prev,
                                visibility: "",
                                shared_roles: "",
                            }));
                        }}
                        onSharedRolesChange={(value) => {
                            setFormData((prev) => ({ ...prev, shared_roles: value }));
                            setErrors((prev) => ({ ...prev, shared_roles: "" }));
                        }}
                    />

                    <SettingsCard
                        icon={<Cpu className="size-4 text-primary" />}
                        title="Behaviour"
                        subtitle="Its persona on every reply — and the lines it never crosses."
                    >
                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <Label className="font-semibold">Persona</Label>
                                <Textarea
                                    value={formData.persona}
                                    onChange={(event) =>
                                        handleInputChange("persona", event.target.value)
                                    }
                                    rows={6}
                                    placeholder="e.g. A calm chief-of-staff. Leads with the headline, keeps numbers in context, always ends with what needs a decision."
                                    className={`resize-y ${errors.persona ? "border-destructive" : ""}`}
                                />
                                {errors.persona ? (
                                    <p className="text-xs text-destructive">{errors.persona}</p>
                                ) : null}
                            </div>
                            <div className="space-y-1.5">
                                <Label className="font-semibold">Restrictions</Label>
                                <Textarea
                                    value={formData.restrictions}
                                    onChange={(event) =>
                                        handleInputChange("restrictions", event.target.value)
                                    }
                                    rows={5}
                                    placeholder="e.g. Only report figures that exist in team activity data. Never speculate on individual performance."
                                    className={`resize-y ${errors.restrictions ? "border-destructive" : ""}`}
                                />
                                {errors.restrictions ? (
                                    <p className="text-xs text-destructive">{errors.restrictions}</p>
                                ) : null}
                            </div>
                        </div>
                    </SettingsCard>

                    <div className="flex items-center justify-end gap-4 pb-8 pt-2">
                        <button
                            type="button"
                            onClick={() => router.push(listPath)}
                            className="text-sm font-medium text-foreground transition-opacity hover:opacity-70"
                        >
                            Cancel
                        </button>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="h-10 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                        >
                            {createAgentMutation.isPending ? (
                                <>
                                    <Loader2 className="mr-1.5 size-4 animate-spin" />
                                    Creating…
                                </>
                            ) : (
                                <>
                                    <Bot className="mr-1.5 size-4" />
                                    Create agent
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </AnimatedPage>
    );
}
